import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, AlertTriangle } from "lucide-react";
import { useConfig } from "@/hooks/useConfig";
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown";
import { fmtDate, istToday } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const HEALTH_COLORS = { Green: "bg-green-100 text-green-800", Amber: "bg-amber-100 text-amber-800", Red: "bg-red-100 text-red-800" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (key) => {
  const [y, m] = String(key || "").split("-");
  return MONTHS[Number(m) - 1] ? `${MONTHS[Number(m) - 1]} ${y}` : key;
};

export default function PnLAuditPage() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const perms = user?.permissions || [];
  const canExport = roles.includes("Admin") || roles.includes("admin") || roles.includes("Finance Manager") || perms.includes("reports.pnl_audit_export");
  const { getValues } = useConfig(["channel_types"]);
  const TRADE_FORMATS = getValues("channel_types");

  const today = istToday();
  const monthStart = today.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [tfFilter, setTfFilter] = useState([]);
  const [chFilter, setChFilter] = useState([]);
  const [healthFilter, setHealthFilter] = useState("all");
  // FOLLOW-UP 1 (F1c): one filter instead of the two inclusion checkboxes. Off = every invoice
  // dated in the period, whatever stage the order reached after the invoice.
  const [grnOnly, setGrnOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/channels").then(r => setChannels(r.data || [])).catch(() => {}); }, []);
  const filteredCh = tfFilter.length > 0 ? channels.filter(c => tfFilter.includes(c.type)) : channels;
  const chOptions = filteredCh.filter(c => c.status === "active").map(c => ({ value: c._id, label: c.name, group: c.type || "" }));

  const loadWith = useCallback(async (override = {}) => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (tfFilter.length) params.trade_format = tfFilter.join(",");
      if (chFilter.length) params.sales_channel = chFilter.join(",");
      if (healthFilter !== "all") params.margin_health = healthFilter;
      params.grn_only = grnOnly;
      Object.assign(params, override);
      const { data: d } = await api.get("/reports/pnl-audit", { params });
      setData(d); setPage(1);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, tfFilter, chFilter, healthFilter, grnOnly]);

  const load = useCallback(() => loadWith(), [loadWith]);

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // FOLLOW-UP 1 (F1c): ticking 'Show GRN'd only' reloads straight away. The reload is fired by the
  // tick itself, so nothing can swallow it.
  const toggleGrnOnly = (checked) => {
    setGrnOnly(checked);
    loadWith({ grn_only: checked });
  };

  // FOLLOW-UP 1 (F1c): Reset clears every filter and reloads, tick or no tick
  const doReset = () => {
    setDateFrom(monthStart); setDateTo(today); setTfFilter([]); setChFilter([]);
    setHealthFilter("all"); setGrnOnly(false);
    loadWith({ date_from: monthStart, date_to: today, grn_only: false,
               trade_format: undefined, sales_channel: undefined, margin_health: undefined });
  };

  const doExport = async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (tfFilter.length) params.trade_format = tfFilter.join(",");
      if (chFilter.length) params.sales_channel = chFilter.join(",");
      params.grn_only = grnOnly;
      if (healthFilter !== "all") params.margin_health = healthFilter;
      const r = await api.get("/reports/pnl-audit/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `PnL_Audit_Report_${dateFrom}_to_${dateTo}.xlsx`; a.click();
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
  };

  const s = data?.summary || {};
  const orders = data?.orders || [];
  const PER_PAGE = 25;
  const pagedOrders = orders.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(orders.length / PER_PAGE);

  return (
    <div data-testid="pnl-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">PnL Audit Report</h1>
          <p className="text-sm text-zinc-500 mt-1">Invoice-date based profitability analysis</p>
        </div>
        {canExport && <Button onClick={doExport} variant="outline" className="rounded-sm h-8 text-xs" data-testid="pnl-export-btn"><Download size={14} /> Download Excel</Button>}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div><Label className="text-[10px] text-zinc-500">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs rounded-sm w-36" /></div>
        <div><Label className="text-[10px] text-zinc-500">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs rounded-sm w-36" /></div>
        <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tfFilter} onChange={v => { setTfFilter(v); setChFilter([]); }} />
        <MultiSelectDropdown label="Sales Channel" options={chOptions} selected={chFilter} onChange={setChFilter} groupBy="group" />
        <div className="flex gap-1">
          {["all", "Green", "Amber", "Red"].map(h => (
            <Button key={h} variant={healthFilter === h ? "default" : "outline"} className={`h-8 text-[10px] px-2 rounded-sm ${healthFilter === h ? "bg-zinc-900 text-white" : ""}`} onClick={() => setHealthFilter(h)}>{h === "all" ? "All" : h}</Button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-[11px] text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={grnOnly} onChange={e => toggleGrnOnly(e.target.checked)} data-testid="pnl-grn-only" />
          Show GRN&apos;d only
        </label>
        <Button onClick={load} className="h-8 text-xs rounded-sm" data-testid="pnl-apply">Apply</Button>
        <Button variant="ghost" onClick={doReset} className="h-8 text-[10px] text-zinc-500" data-testid="pnl-reset">Reset</Button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pnl-summary-cards">
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Revenue (excl. GST)</p><p className="text-lg font-mono font-bold">{fmt(s.revenue_excl_gst)}</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Total COGS</p><p className="text-lg font-mono font-bold text-red-600">{fmt(s.total_cogs)}</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Net Margin</p><p className={`text-lg font-mono font-bold ${(s.net_margin || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(s.net_margin)}</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Net Margin %</p><p className={`text-lg font-mono font-bold ${(s.net_margin_pct || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{s.net_margin_pct || 0}%</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Total Orders</p><p className="text-lg font-mono font-bold">{s.total_orders || 0}</p></div>
        </div>
      )}

      {/* BATCH 4 (4.2e): logistics split + whether the period is final */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pnl-logistics-cards">
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Logistics — Actual</p><p className="text-sm font-mono font-bold" data-testid="pnl-freight-actual">{fmt(s.logistics_breakdown?.actual_amount)}</p><p className="text-[10px] text-zinc-400">{s.logistics_breakdown?.actual_orders || 0} orders</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Logistics — Estimated</p><p className="text-sm font-mono font-bold text-amber-700" data-testid="pnl-freight-estimated">{fmt(s.logistics_breakdown?.estimated_amount)}</p><p className="text-[10px] text-zinc-400">{s.logistics_breakdown?.estimated_orders || 0} orders</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Logistics — Missing</p><p className="text-sm font-mono font-bold text-red-600" data-testid="pnl-freight-missing">{s.logistics_breakdown?.missing_orders || 0}</p><p className="text-[10px] text-zinc-400">orders with no freight</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Estimate vs Actual</p><p className="text-sm font-mono font-bold" data-testid="pnl-freight-variance">{fmt(s.logistics_breakdown?.estimate_vs_actual?.variance)}</p><p className="text-[10px] text-zinc-400">RTO freight {fmt(s.logistics_breakdown?.rto_freight)}</p></div>
          <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">TDS (not deducted)</p><p className="text-sm font-mono font-bold" data-testid="pnl-tds">{fmt(s.tds_shown)}</p><p className="text-[10px] text-zinc-400">Deductions {fmt(s.deductions)}</p></div>
        </div>
      )}

      {data && (s.months || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="pnl-month-status">
          {(s.months || []).map(m => (
            <span key={m.month} className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${m.status === "FINAL" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid={`pnl-month-${m.month}`}>
              {fmtMonth(m.month)} — {m.status}{m.status === "PROVISIONAL" ? ` (${m.freight_estimated} estimated, ${m.freight_missing} missing)` : ""}
            </span>
          ))}
        </div>
      )}

      {/* Data Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="pnl-audit-table">
        <table className="data-table w-full">
          <thead><tr><th>Order #</th><th>Invoice Date</th><th>Customer</th><th>Trade Format</th><th>Sales Channel</th><th>SKU</th><th className="num">Qty billed</th><th className="num">Price (excl.)</th><th className="num">Line Value</th><th className="num">COGS</th><th className="num">Gross Margin</th><th className="num">Net Margin</th><th className="num">NM %</th><th>Freight</th><th>Health</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={15} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            pagedOrders.length === 0 ? <tr><td colSpan={15} className="text-center py-8 text-zinc-400 text-xs">No invoiced orders in selected period</td></tr> :
            pagedOrders.map((o, oi) => (
              <>{o.sku_lines.map((sl, si) => (
                <tr key={`${o.order_no}-${si}`} className={si === 0 ? "border-t border-zinc-200" : ""}>
                  {si === 0 && <td rowSpan={o.sku_lines.length + 1} className="text-xs font-mono font-semibold align-top border-r border-zinc-100"><button onClick={() => window.open(`/orders/${o.order_id}`, '_blank')} className="text-blue-600 hover:underline">{o.order_no}</button><br/><span className="text-[9px] text-zinc-400">{fmtDate(o.invoice_date)}</span><br/><span className={`text-[10px] ${o.pnl_group === "GRN'd" ? "text-emerald-700" : o.pnl_group === "Dispatched, GRN pending" ? "text-amber-700" : "text-zinc-500"}`} data-testid={`pnl-row-group-${o.order_no}`}>{o.pnl_group}</span></td>}
                  <td className="text-[10px]">{si === 0 ? fmtDate(o.invoice_date) : ""}</td>
                  <td className="text-xs">{si === 0 ? o.customer_name : ""}</td>
                  <td className="text-[10px]">{si === 0 ? o.trade_format : ""}</td>
                  <td className="text-[10px]">{si === 0 ? o.sales_channel : ""}</td>
                  <td className="text-xs">{sl.sku_name}</td>
                  <td className="num font-mono text-xs">{sl.qty}</td>
                  <td className="num font-mono text-xs">{fmt(sl.selling_price_base)}</td>
                  <td className="num font-mono text-xs">{fmt(sl.line_value_excl_gst)}</td>
                  <td className="num font-mono text-xs text-zinc-500" data-testid={`pnl-cogs-${sl.sku_code}`}>{sl.line_cogs == null ? '—' : sl.cogs_missing ? <span className="text-red-600 font-semibold">COGS missing</span> : fmt(sl.line_cogs)}</td>
                  <td className="num font-mono text-xs">{sl.line_gross_margin != null ? fmt(sl.line_gross_margin) : '—'}</td>
                  <td className="num"></td><td className="num"></td><td></td><td></td>
                </tr>
              ))}
              <tr key={`${o.order_no}-total`} className="bg-zinc-50 font-semibold border-b border-zinc-200">
                <td className="text-[10px] text-zinc-500" colSpan={5}>Order Total</td>
                <td></td><td className="num font-mono text-xs">{o.sku_lines.reduce((s,l) => s + l.qty, 0)}</td><td></td>
                <td className="num font-mono text-xs">{fmt(o.order_totals.order_value_excl_gst)}</td>
                <td className="num font-mono text-xs text-zinc-500">{o.order_totals.total_cogs != null ? fmt(o.order_totals.total_cogs) : '—'}</td>
                <td className="num font-mono text-xs">{o.order_totals.gross_margin != null ? fmt(o.order_totals.gross_margin) : '—'}</td>
                <td className={`num font-mono text-xs font-bold ${(o.order_totals.net_margin || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(o.order_totals.net_margin)}</td>
                <td className="num font-mono text-xs">{o.order_totals.net_margin_pct}%</td>
                <td className="text-[10px]" data-testid={`pnl-freight-src-${o.order_no}`}>
                  <span className={o.order_totals.freight_source === "Actual" ? "text-green-700" : o.order_totals.freight_source === "Estimated" ? "text-amber-700" : "text-red-600"}>{o.order_totals.freight_source}</span>
                  <span className="block font-mono text-zinc-500">{fmt(o.order_totals.logistics_cost)}</span>
                  {o.order_totals.rto_freight > 0 && <span className="block text-[9px] text-zinc-400">RTO {fmt(o.order_totals.rto_freight)}</span>}
                </td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${HEALTH_COLORS[o.order_totals.margin_health] || ''}`}>{o.order_totals.margin_health}</span></td>
              </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* FOLLOW-UP 1 (F1b/F1d): the three groups, then the grand total */}
      {data && (s.groups || []).length > 0 && (
        <div className="border border-zinc-200 mt-3 overflow-x-auto" data-testid="pnl-group-subtotals">
          <table className="data-table w-full">
            <thead><tr><th>Group</th><th className="num">Orders</th><th className="num">Revenue (excl. GST)</th><th className="num">COGS</th><th className="num">Logistics</th><th className="num">Deductions</th><th className="num">Credit notes</th><th className="num">Net margin</th></tr></thead>
            <tbody>
              {(s.groups || []).map(g => (
                <tr key={g.group} data-testid={`pnl-group-${g.group.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                    className={g.group === "Dispatched, GRN pending" ? "bg-amber-50" : g.group === "Invoiced, not yet dispatched" ? "bg-zinc-100 text-zinc-600" : ""}>
                  <td className="text-xs font-semibold">{g.group}</td>
                  <td className="num font-mono text-xs">{g.orders}</td>
                  <td className="num font-mono text-xs font-semibold">{fmt(g.revenue)}</td>
                  <td className="num font-mono text-xs">{fmt(g.cogs)}</td>
                  <td className="num font-mono text-xs">{fmt(g.logistics)}</td>
                  <td className="num font-mono text-xs">{fmt(g.deductions)}</td>
                  <td className="num font-mono text-xs">{fmt(g.credit_notes)}</td>
                  <td className="num font-mono text-xs">{fmt(g.net_margin)}</td>
                </tr>
              ))}
              {/* PASS 2 (A4): the company-level lines that are already inside the grand-total net
                  margin but were only in the payload before. */}
              <tr className="bg-zinc-50" data-testid="pnl-line-marketing">
                <td className="text-xs">Marketing (platform invoices)</td>
                <td className="num font-mono text-[10px] text-zinc-400" colSpan={6}>company level · GST claimed back, so profit falls by the amount without GST</td>
                <td className="num font-mono text-xs text-red-600">{s.marketing_deductions ? `(${fmt(s.marketing_deductions)})` : fmt(0)}</td>
              </tr>
              <tr className="bg-zinc-50" data-testid="pnl-line-consumer-discount">
                <td className="text-xs">Promo / consumer discount</td>
                <td className="num font-mono text-[10px] text-zinc-400" colSpan={6}>company level · the whole amount hits profit</td>
                <td className="num font-mono text-xs text-red-600">{s.consumer_discounts ? `(${fmt(s.consumer_discounts)})` : fmt(0)}</td>
              </tr>
              <tr className="bg-zinc-900 text-white" data-testid="pnl-grand-total">
                <td className="text-xs font-bold">GRAND TOTAL</td>
                <td className="num font-mono text-xs">{s.total_orders || 0}</td>
                <td className="num font-mono text-xs font-bold">{fmt(s.revenue_excl_gst)}</td>
                <td className="num font-mono text-xs">{s.total_cogs == null ? "—" : fmt(s.total_cogs)}</td>
                <td className="num font-mono text-xs">{fmt(s.logistics_cost)}</td>
                <td className="num font-mono text-xs">{fmt(s.deductions)}</td>
                <td className="num font-mono text-xs">{fmt(s.credit_notes)}</td>
                <td className="num font-mono text-xs font-bold">{fmt(s.net_margin)}</td>
              </tr>
            </tbody>
          </table>
          {(s.orders_outside_the_three_lists || []).length > 0 && (
            <p className="text-[10px] text-amber-700 p-2" data-testid="pnl-off-list">
              {(s.orders_outside_the_three_lists || []).length} invoiced order(s) at a status outside the three
              lists are counted under &quot;Invoiced, not yet dispatched&quot;:{" "}
              {(s.orders_outside_the_three_lists || []).map(o => `${o.order_no} (${o.status})`).join(", ")}
            </p>
          )}
        </div>
      )}

      {/* Pagination + Footer */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-7 text-[10px] rounded-sm">Prev</Button>
          <span className="text-[10px] text-zinc-500">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-7 text-[10px] rounded-sm">Next</Button>
        </div>
      )}
      <div className="mt-3 text-[10px] text-zinc-400">
        <p>Showing {pagedOrders.length} of {orders.length} invoiced orders for {fmtDate(dateFrom)} to {fmtDate(dateTo)}</p>
        <p data-testid="pnl-report-status">Report status: <b>{s.report_status || "—"}</b>{s.cogs_missing_lines > 0 ? ` · ${s.cogs_missing_lines} lines with no frozen COGS` : ""}</p>
        {s.pending_invoice_count > 0 && <p className="text-amber-600 mt-0.5"><AlertTriangle size={10} className="inline mr-1" />Pending Invoice: {s.pending_invoice_count} orders worth {fmt(s.pending_invoice_value)} not included</p>}
      </div>
    </div>
  );
}
